import { get, post } from '@axios';
import type { HttpResponse } from '@capacitor/core';
import qs from 'qs';
import { PAGINATION_CONSTANTS } from '@/configs/constants';
import {
    normalizeAd,
    normalizeOpiniao,
    normalizePost,
    normalizeTv,
} from './normalize';

const API_PREFIX = '/api/app/v1';

function mapResponseData(res: HttpResponse, mapper: (data: any) => any): HttpResponse {
    return {
        ...res,
        data: mapper(res.data),
    };
}

export async function fetchPosts(query: {
    page?: number;
    per_page?: number;
    perPage?: number;
    categories?: string | number;
    categoryWpIds?: string | number;
}): Promise<HttpResponse> {
    const categoryWpIds =
        query.categoryWpIds ?? query.categories ?? '';
    const queryString = qs.stringify({
        categoryWpIds,
        page: query.page ?? 1,
        perPage: query.perPage ?? query.per_page ?? PAGINATION_CONSTANTS.DEFAULT_POSTS_PER_PAGE,
    });
    const res = await get(`${API_PREFIX}/posts?${queryString}`);
    return mapResponseData(res, (data) => {
        const byCategoryRaw = (data?.byCategory ?? {}) as Record<
            string,
            { items?: unknown[]; pagination?: { total?: number; page?: number; perPage?: number } }
        >;
        const byCategory: Record<string, { items: ReturnType<typeof normalizePost>[]; pagination: unknown }> = {};
        const legacy: Record<string, { posts: unknown[]; pagination: unknown }> = {};
        for (const [key, value] of Object.entries(byCategoryRaw)) {
            const items = (value.items ?? []).map((item) => normalizePost(item as Parameters<typeof normalizePost>[0]));
            legacy[`_${key}`] = {
                posts: items,
                pagination: {
                    total: value.pagination?.total ?? 0,
                    limit: value.pagination?.perPage ?? query.per_page,
                    page: value.pagination?.page ?? query.page ?? 1,
                    perPage: value.pagination?.perPage,
                },
            };
            byCategory[key] = {
                items,
                pagination: value.pagination,
            };
        }
        return { byCategory, ...legacy };
    });
}

export async function fetchPost(id: number): Promise<HttpResponse> {
    const res = await get(`${API_PREFIX}/posts/${id}`);
    return mapResponseData(res, (data) => (data ? normalizePost(data) : data));
}

export async function searchPosts(query: any, config: any = {}): Promise<HttpResponse> {
    const queryString = qs.stringify({
        search: query.search,
        page: query.page ?? 1,
        perPage: query.per_page ?? query.perPage,
    });
    const res = await get(`${API_PREFIX}/search?${queryString}`, config);
    return mapResponseData(res, (data) => {
        const items = (data?.items ?? []).map(normalizePost);
        return {
            items,
            posts: items,
            pagination: {
                total: data?.pagination?.total ?? 0,
                page: data?.pagination?.page ?? 1,
                perPage: data?.pagination?.perPage,
                limit: data?.pagination?.perPage,
            },
        };
    });
}

export async function fetchTvPosts(query: any): Promise<HttpResponse> {
    const queryString = qs.stringify({
        page: query.page ?? 1,
        perPage: query.per_page ?? query.perPage ?? 100,
    });
    const res = await get(`${API_PREFIX}/tv24h?${queryString}`);
    return mapResponseData(res, (data) => {
        const items = (data?.items ?? []).map(normalizeTv);
        return {
            items,
            posts: items,
            pagination: data?.pagination ?? { total: 0, page: 1, perPage: 100 },
        };
    });
}

export async function subscribeToCategories(deviceToken: string, categoryIds: number[]) {
    return post(`${API_PREFIX}/subscriptions`, {
        deviceToken,
        categoryWpIds: categoryIds,
    });
}

export async function fetchNotifications(query: {
    deviceToken: string;
    page?: number;
    perPage?: number;
}) {
    const queryString = qs.stringify({
        deviceToken: query.deviceToken,
        page: query.page || 1,
        perPage: query.perPage || PAGINATION_CONSTANTS.DEFAULT_NOTIFICATIONS_PER_PAGE,
    });
    const res = await get(`${API_PREFIX}/notifications?${queryString}`);
    return mapResponseData(res, (data) => ({
        total: data?.pagination?.total ?? 0,
        page: data?.pagination?.page ?? 1,
        per_page: data?.pagination?.perPage,
        notifications: (data?.items ?? []).map((n: any) => ({
            id: n.id,
            notification: n.notification,
            sent_at: n.sentAt,
            sentAt: n.sentAt,
            date: n.sentAt,
        })),
    }));
}

export async function deleteNotifications(deviceToken: string, notificationIds: number[]) {
    return post(`${API_PREFIX}/notifications/delete`, {
        deviceToken,
        ids: notificationIds,
    });
}

export async function fetchPostByUrl(url: string): Promise<HttpResponse> {
    const queryString = qs.stringify({ url });
    const res = await get(`${API_PREFIX}/resolve?${queryString}`);
    return mapResponseData(res, (data) => {
        if (!data) return data;
        return {
            ...data,
            post_id: data.wpId,
            post_type: data.type === 'opiniao' ? 'opinion' : 'post',
            categories: data.categoryWpIds ?? [],
        };
    });
}

export async function fetchAds(): Promise<HttpResponse> {
    const res = await get(`${API_PREFIX}/ads`);
    return mapResponseData(res, (data) => {
        const items = (data?.items ?? []).map(normalizeAd);
        return items;
    });
}

export async function fetchOpinions(query: any): Promise<HttpResponse> {
    const queryString = qs.stringify({
        page: query.page ?? 1,
        perPage: query.per_page ?? query.perPage,
    });
    const res = await get(`${API_PREFIX}/opinioes?${queryString}`);
    return mapResponseData(res, (data) => {
        const items = (data?.items ?? []).map(normalizeOpiniao);
        return {
            items,
            posts: items,
            pagination: {
                total: data?.pagination?.total ?? 0,
                page: data?.pagination?.page ?? 1,
                perPage: data?.pagination?.perPage,
                limit: data?.pagination?.perPage,
            },
        };
    });
}

export async function fetchOpinionById(id: number): Promise<HttpResponse> {
    const res = await get(`${API_PREFIX}/opinioes/${id}`);
    return mapResponseData(res, (data) => (data ? normalizeOpiniao(data) : data));
}

export async function fetchPostUpdatedAt(id: number): Promise<HttpResponse> {
    const res = await get(`${API_PREFIX}/posts/${id}/updated-at`);
    return mapResponseData(res, (data) => ({
        wpId: data?.wpId,
        updated_at: data?.updatedAt,
        updatedAt: data?.updatedAt,
    }));
}

export async function fetchOpinionUpdatedAt(id: number): Promise<HttpResponse> {
    const res = await get(`${API_PREFIX}/opinioes/${id}/updated-at`);
    return mapResponseData(res, (data) => ({
        wpId: data?.wpId,
        updated_at: data?.updatedAt,
        updatedAt: data?.updatedAt,
    }));
}
